import { makeAutoObservable, runInAction } from "mobx";
import { v4 as uuidv4 } from "uuid";
import { AspectRatio } from "../../components/common/DropDowns/StaticDropdown";
import { BackendError } from "../../config/axios";
import { LABS } from "../../constants/AppsConstants";
import { FIBO_DEFAULT_NUM_RESULTS } from "../../constants/FiboConstants";
import { POSTHOG_EVENTS } from "../../constants/PostHogEvents";
import { IRootStore } from "../../mobx/root-store";
import { PlaygroundResult } from "../../models/playground";
import postHogService from "../../services/PostHogService";
import { urlToFile } from "../../utils";
import QueryService from "../../utils/QueryService";
import { showErrorToast } from "../../utils/toast";
import useImageUtils from "../../utils/useImageUtils";
import MaskEditorStore, { IMaskEditorStore } from "./MaskEditor/mask-editor-store";

export interface IFiboEditStore {
	maskEditorStore: IMaskEditorStore;

	abortController: AbortController;

	config: FiboEditConfigType;
	get isConfigValid(): boolean;

	isGeneratingImages: boolean;
	isGeneratingStructuredPrompt: boolean;
	openMaskEditor: boolean;
	errors: { generate?: BackendError };

	updateImage(image: File | string | undefined): Promise<void>;
	resetStore(): void;
	edit(): Promise<void>;
	stopEditImageGeneration(): void;
	createUploadedImageGroup: (imageUrl: string) => void;
}

export interface FiboEditConfigType {
	image: string;
	num_results: number;
	prompt?: string;
	mask?: any;
	aspect_ratio?: AspectRatio;
	structured_instruction?: string;
	structured_prompt?: string;
	structured_prompt_diff?: string;
	negative_prompt?: string;
	guidance_scale?: number;
	model_version?: string;
	seed?: number;
	steps_num?: number;
	ip_signal?: boolean;
	// prompt_content_moderation: boolean;
	// visual_input_content_moderation: boolean;
	// visual_output_content_moderation: boolean;
}

export interface FiboEditConfigTypeRequest extends Omit<FiboEditConfigType, "image" | "mask" | "prompt"> {}

export interface ContentModeration {
	prompt?: boolean;
	input?: boolean;
	output?: boolean;
}

export interface GenerateStructuredInstructionRequestPayload {
	ip_signal: boolean;
	prompt_content_moderation: boolean;
	visual_input_content_moderation: boolean;
	instruction: string;
	seed?: number;
}

export class FiboEditStore implements IFiboEditStore {
	private rootStore: IRootStore;
	private fiboEditService: QueryService = new QueryService("/image/edit");
	private imageUtils = useImageUtils();

	maskEditorStore: IMaskEditorStore = new MaskEditorStore();

	abortController: AbortController = new AbortController();
	config: FiboEditConfigType = {
		image: "",
		num_results: FIBO_DEFAULT_NUM_RESULTS,
		prompt: "",
		mask: undefined,
		steps_num: 30,
		seed: undefined,
		negative_prompt: "",
	};

	lastGeneratedResultId: string | null = null;
	isGeneratingImages: boolean = false;
	isGeneratingStructuredPrompt: boolean = false;
	openMaskEditor: boolean = false;
	errorMessage: string | null = null;
	errors: { generate?: BackendError } = {};

	constructor(rootStore: IRootStore) {
		makeAutoObservable(this);

		this.rootStore = rootStore;
		this.resetStore();
	}

	get steps(): number | undefined {
		return this.rootStore.textToImageStore.config.steps_num;
	}

	set steps(value: number | undefined) {
		this.rootStore.textToImageStore.config.steps_num = value;
	}

	get isConfigValid(): boolean {
		const hasRequiredFields: boolean = !!(this.config.image && this.config.prompt);
		if (!hasRequiredFields && !this.config.structured_instruction && !this.config.prompt) {
			return false;
		}

		return true;
	}

	resetStore(): void {
		this.resetConfig();

		this.isGeneratingImages = false;
		this.isGeneratingStructuredPrompt = false;
	}

	resetConfig(): void {
		this.config = {
			image: "",
			num_results: FIBO_DEFAULT_NUM_RESULTS,
			prompt: "",
			mask: undefined,
			steps_num: 30,
			seed: undefined,
			negative_prompt: "",
		};
	}

	async updateImage(image: File | string | undefined): Promise<void> {
		if (!image) return;

		if (typeof image === "string") {
			runInAction(() => {
				this.config.image = image;
				this.config.mask = undefined;
				this.maskEditorStore.resetBrushConfigs();
			});
			return;
		}

		const dataUrl: string | ArrayBuffer | null = await this.imageUtils.getBase64(image as Blob);
		runInAction(() => {
			this.config.image = dataUrl as string;
			this.config.mask = undefined;
			this.maskEditorStore.resetBrushConfigs();
		});
	}

	stopEditImageGeneration() {
		const { playgroundStore } = this.rootStore;

		this.abortController.abort();
		this.abortController = new AbortController();

		this.isGeneratingImages = false;
		this.isGeneratingStructuredPrompt = false;
		playgroundStore.removeNotLoadingImages();

		const lastResult = this.lastGeneratedResultId
			? playgroundStore.playgroundResults.find((r) => r.id === this.lastGeneratedResultId)
			: null;

		if (lastResult) {
			playgroundStore.selectedResult = lastResult;
		}
	}

	private showLoadingSkeletons() {
		const { playgroundStore } = this.rootStore;
		const resultsSkeletons: PlaygroundResult = {
			id: uuidv4(),
			config: { ...this.config } as FiboEditConfigType,
			type: LABS.FIBO_EDIT,
			images: Array.from({ length: this.config.num_results }).map(() => ({
				id: uuidv4(),
				url: "",
				seed: NaN,
				loading: true,
				playground_result_id: "",
				aspect_ratio: "1:1",
			})),
		};
		playgroundStore.playgroundResults = [...playgroundStore.playgroundResults, resultsSkeletons];

		const savedIndex = playgroundStore.playgroundResults.length - 1;
		return savedIndex;
	}

	private async generateFromStructuredPrompt(seed: number): Promise<any> {
		if (!this.isConfigValid) throw new Error("Config is not valid");

		const image = await urlToFile(this.config.image, "image-to-edit");
		const mask = this.config.mask ? await urlToFile(this.config.mask, "mask") : undefined;

		const { image: _, mask: __, prompt: ___, ...payload } = this.config;
		const configPayload = { ...payload }; // Using all the properties except the files
		configPayload.seed = seed;

		const formData = new FormData();
		formData.append("image", image);
		mask && formData.append("mask", mask);
		formData.append("config", JSON.stringify(configPayload));

		const response = await this.fiboEditService.post(
			"/",
			formData,
			{ "Content-Type": "multipart/form-data" },
			{ signal: this.abortController.signal },
		);

		return response;
	}

	private async pollStatusResposnes(
		statusResponse: PromiseSettledResult<any>,
		results: PlaygroundResult,
		index: number,
	): Promise<void> {
		const { playgroundStore } = this.rootStore;
		const image = results.images[index];

		if (statusResponse.status !== "fulfilled") {
			runInAction(() => {
				image.error = { type: "unavailable" };
				image.loading = false;
			});
			return;
		}

		try {
			const result: any = await this.imageUtils.pullUntilAvailableV2("", {
				statusUrl: statusResponse.value.status_url,
				isV2: true,
			});
			runInAction(() => {
				image.url = result.image_url;
				image.aspect_ratio = "auto";
				image.seed = result.seed;
				image.type = LABS.FIBO_EDIT;
			});
			playgroundStore.onSuccessResult(image);

			// Consume an anonymous token when the image is actually delivered
			const { authStore, anonymousUsageStore } = this.rootStore;
			if (!authStore.isLoggedIn) {
				anonymousUsageStore.consumeToken("labs_edit");
			}
		} catch (ex) {
			console.error("image polling failed:", ex);
			runInAction(() => {
				image.error = { type: "unavailable" };
				image.loading = false;
			});
		} finally {
			const allDone = results.images.every((img) => !img.loading);
			if (allDone) {
				runInAction(() => {
					this.isGeneratingImages = false;
				});

				// Track generation completed when all images are done
				const successfulImages = results.images.filter((img) => img.url && !img.error).length;
				postHogService.trackEvent(
					POSTHOG_EVENTS.FIBO_EDIT_GENERATE_COMPLETED,
					{
						lab_variant: "FIBO-EDIT",
						num_results: results.images.length,
						successful_images: successfulImages,
						failed_images: results.images.length - successfulImages,
					},
					{ send_instantly: true },
				);
			}
		}
	}

	private handleEditError(err: any, resultIndex: number): void {
		const { playgroundStore, anonymousUsageStore } = this.rootStore;

		const isAnonymousUsageLimit =
			err?.response?.status === 429 && err?.response?.data?.code === "ANONYMOUS_USAGE_LIMIT_REACHED";

		if (isAnonymousUsageLimit) {
			const feature = err?.response?.data?.feature || "labs_edit";
			runInAction(() => {
				this.isGeneratingImages = false;
				this.isGeneratingStructuredPrompt = false;
				this.lastGeneratedResultId = null;
				playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
					(_, index) => index !== resultIndex,
				);
			});
			playgroundStore.removeNotLoadingImages();
			anonymousUsageStore.setLimitReachedFeature(feature);
			return;
		}

		const errorMessage = err.response?.data?.error?.message || err.response?.data?.message || "";
		const errorDetails = err.response?.data?.error?.details || "";
		const isPromptContentModeration =
			errorMessage.toLowerCase().includes("prompt did not pass content moderation") ||
			errorDetails.toLowerCase().includes("prompt did not pass content moderation");

		runInAction(() => {
			this.isGeneratingImages = false;
			this.isGeneratingStructuredPrompt = false;
			this.lastGeneratedResultId = null;

			if (isPromptContentModeration) {
				this.errors.generate = {
					message: "textToImagePlaygroundPromptContentModeration",
					status: err.response?.status || 422,
				};
			} else {
				const errorMessage = "Image generation failed";
				showErrorToast(errorMessage);
				console.error("error:", err);
			}

			playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
				(_, index) => index !== resultIndex,
			);
		});

		// Clean up any loading images
		playgroundStore.removeNotLoadingImages();
	}

	async edit(): Promise<void> {
		const { playgroundStore, authStore, anonymousUsageStore } = this.rootStore;
		const resultIndex = playgroundStore.playgroundResults.length;

		// Cap num_results to remaining tokens for anonymous users
		if (!authStore.isLoggedIn) {
			const feature = "labs_edit";
			const remaining = anonymousUsageStore.getRemaining(feature);
			if (remaining <= 0) {
				anonymousUsageStore.setLimitReachedFeature(feature);
				return;
			}
			if (this.config.num_results > remaining) {
				this.config.num_results = remaining;
			}
		}

		// Reset the errors
		runInAction(() => {
			this.errors = { ...this.errors, generate: undefined };
		});

		// Track generation started
		postHogService.trackEvent(
			POSTHOG_EVENTS.FIBO_EDIT_GENERATE_STARTED,
			{
				lab_variant: "FIBO-EDIT",
				prompt: this.config.prompt,
				num_results: this.config.num_results,
				has_mask: !!this.config.mask,
				steps: this.config.steps_num,
				seed: this.config.seed,
			},
			{ send_instantly: true },
		);

		try {
			this.isGeneratingImages = true;
			this.isGeneratingStructuredPrompt = true;

			// Auto-save the mask from the canvas if the editor has been used
			try {
				const maskFileBase64 = await this.maskEditorStore.getMaskFileBase64();
				if (maskFileBase64) {
					this.config.mask = maskFileBase64;
				}
			} catch (error) {
				console.warn("Could not save mask automatically:", error);
			}

			this.showLoadingSkeletons();

			// Get the previous structured prompt for diff tracking
			// const selectedGroup = fiboStore.getSelectedFiboGroup();
			// #TODO: add the StructuredPrompt diff feature
			// const previousStructuredPrompt = selectedGroup?.structured_prompt;

			const { seed, structured_instruction } = await this.generateStructuredPrompt();
			if (!structured_instruction) {
				this.errorMessage = "Structured instruction generation failed";
				this.stopEditImageGeneration();
				return;
			}

			runInAction(() => {
				// Update this.config.structured_prompt immediately so the component can display it
				this.config.structured_instruction = structured_instruction;
				this.config.structured_prompt = structured_instruction;

				// Save to result config (for selectedGroup.structured_prompt)
				const currentPlaygroundResults = playgroundStore.playgroundResults[resultIndex];
				if (currentPlaygroundResults) {
					(currentPlaygroundResults.config as any).structured_instruction = structured_instruction;
					(currentPlaygroundResults.config as any).structured_prompt = structured_instruction;
					// Save the diff if there was a previous structured prompt
					// if (previousStructuredPrompt) {
					// 	(currentPlaygroundResults.config as any).structured_prompt_diff = previousStructuredPrompt;
					// }
					if (seed !== undefined && seed !== null) {
						(currentPlaygroundResults.config as FiboEditConfigType).seed = seed;
					}
				}

				this.isGeneratingStructuredPrompt = false;
			});

			const statusResponses = await Promise.allSettled(
				Array.from({ length: this.config.num_results }).map((_, i) =>
					this.generateFromStructuredPrompt(seed + i),
				),
			);

			const results = playgroundStore.playgroundResults[resultIndex];

			// Updating the result config with the structured prompt, diff, and seed
			runInAction(() => {
				const updatedConfig: FiboEditConfigType = {
					...(results.config as FiboEditConfigType),
					structured_instruction: structured_instruction,
					structured_prompt: structured_instruction,
					seed: seed,
				};
				// if (previousStructuredPrompt) {
				// 	updatedConfig.structured_prompt_diff = previousStructuredPrompt;
				// }
				results.config = updatedConfig;
			});

			statusResponses.forEach((statusResponse, index) =>
				this.pollStatusResposnes(statusResponse, results, index),
			);
		} catch (err: any) {
			postHogService.trackEvent(
				POSTHOG_EVENTS.FIBO_EDIT_GENERATE_FAILED,
				{
					lab_variant: "FIBO-EDIT",
					model: "fibo-edit",
					error: err.response?.data?.message || err.message || "Unknown error",
					error_status: err.response?.status,
					version: "v2",
				},
				{ send_instantly: true },
			);

			this.handleEditError(err, resultIndex);
		}
	}

	// WARNING: Content moderation is true by default
	async generateStructuredPrompt(): Promise<any> {
		if (!this.isConfigValid) {
			throw new Error("Config is not valid");
		}

		this.abortController = new AbortController();
		const payload: GenerateStructuredInstructionRequestPayload = {
			ip_signal: true,
			visual_input_content_moderation: true,
			prompt_content_moderation: true,
			instruction: this.config.prompt!,
			seed: this.config.seed,
		};

		const formData = new FormData();
		formData.append("image", await urlToFile(this.config.image, "image-to-upload"));
		if (this.config.mask) {
			formData.append("mask", await urlToFile(this.config.mask, "mask"));
		}
		formData.append("config", JSON.stringify(payload));

		const response = await this.fiboEditService.post(
			"/generate-structured-instruction",
			formData,
			{ "Content-Type": "multipart/form-data" },
			{ signal: this.abortController.signal },
		);
		return response?.result ?? response;
	}

	createUploadedImageGroup = (imageUrl: string): void => {
		runInAction(() => {
			const groupId = `uploaded-${Date.now()}`;

			const results: PlaygroundResult = {
				id: groupId,
				config: { image: imageUrl } as FiboEditConfigType,
				type: LABS.FIBO_EDIT,
				images: Array.from({ length: 1 }).map(() => ({
					id: uuidv4(),
					url: imageUrl,
					seed: 0,
					loading: false,
					playground_result_id: groupId,
					aspect_ratio: "auto",
				})),
			};

			const { playgroundStore } = this.rootStore;
			playgroundStore.playgroundResults = [...playgroundStore.playgroundResults, results];
		});
	};
}
